// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, ActionIcon, Box, Group, Menu, Stack, Text } from '@mantine/core';
import { FC, useContext } from 'react';
import { i18n } from '@lingui/core';
import { IconTag, IconDots, IconBallpen, IconTrash, IconDragDrop, IconDragDrop2 } from '@tabler/icons';
import { useLocalStorage } from '@mantine/hooks';
import { useDrag, useDrop } from 'react-dnd-latest';
import ProductPosition from '../../../../Molecules/Document/Position/ProductPosition';
import { CategoryDto, TaxType } from '../../../../Service/DocumentT';
import { ProductsContext } from '../../../../Service/Context/proxy/ProductsContext';
import { PositionS } from '../../../../Service/PositionS';
import { openRenameCategoryProps } from '../../../../Molecules/Document/Position/AddProductModal';
import { WindowContext } from '../../../../Service/Context/WindowContext';
import EmptyText from '../../../../Atoms/EmptyText';
import LinkText from '../../../../Atoms/LinkText';

interface Props {
  category: CategoryDto;
  previewVersion?: boolean;
}

const CategoryProducts: FC<Props> = ({ category, previewVersion = false }) => {
  const { categories, categoriesHandlers, refreshCategories, products, addProduct, removeGroup } =
    useContext(ProductsContext);
  const [productsTaxType] = useLocalStorage({ key: 'productsTaxType' });
  const { lg } = useContext(WindowContext);

  const { id, name } = category;
  const { applyWhere } = categoriesHandlers;
  const filteredProducts = products.filter((p) => p.categoryId === id);
  const { length } = filteredProducts;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'category',
      canDrop: (item: CategoryDto) => item.id !== id,
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== id,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: CategoryDto) => {
        PositionS.moveCategory(item.id, id).then(refreshCategories);
      },
    }),
    [id],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'category',
      item: category,
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [id],
  );

  const onEdit = () => {
    openRenameCategoryProps({
      category,
      existingCategories: categories.map((c) => c.name),
      onConfirm: (val) =>
        applyWhere(
          (c) => c.id === id,
          (c) => ({ ...c, name: val }),
        ),
    });
  };

  return (
    <div ref={drop} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Group>
        {lg && (
          <ActionIcon ref={drag}>
            {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
            {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </ActionIcon>
        )}
        <Accordion.Item value={id} sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Accordion.Control>
              <Group>
                <IconTag size={20} style={{ marginRight: 0, marginTop: -3 }} />
                <Stack spacing={0}>
                  <Text className="overflow-ellipsis">{name}</Text>
                  <Text size="xs" color="dimmed">
                    {length === 1 ? '1 Position' : length > 1 ? `${length} Positionen` : 'Keine Positionen'}
                  </Text>
                </Stack>
              </Group>
            </Accordion.Control>
            <Menu withinPortal shadow="md" width={140} transition="scale-y" position="bottom-end">
              <Menu.Target>
                <ActionIcon mr="xs">
                  <IconDots size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item icon={<IconBallpen size={16} />} onClick={onEdit}>
                  Bearbeiten
                </Menu.Item>
                <Menu.Item color="red" icon={<IconTrash size={16} />} onClick={() => removeGroup(id)}>
                  {i18n._('actions.delete')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Box>
          <Accordion.Panel>
            <Stack>
              {filteredProducts.map((product) => (
                <ProductPosition
                  key={product.id}
                  taxType={productsTaxType as TaxType}
                  previewVersion={previewVersion}
                  position={product}
                />
              ))}
            </Stack>
            {length === 0 && <EmptyText />}
            <LinkText mt="xl" onClick={() => addProduct({ category: name })}>
              + Produkt hinzufugen
            </LinkText>
          </Accordion.Panel>
        </Accordion.Item>
      </Group>
    </div>
  );
};

export default CategoryProducts;
